import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { isCustomerAuthenticated } from '../utils/customerAuth';

export interface CartItem {
  productId: string;
  name: string;
  price: number;
  mrp: number;
  image: string;
  quantity: number;
  variant?: {
    color?: string;
    size?: string;
  };
}

const getVariantKey = (variant?: { color?: string; size?: string }) =>
  `${variant?.color || ''}|${variant?.size || ''}`;

const loadStoredCart = (): CartItem[] => {
  if (!isCustomerAuthenticated()) return [];
  try {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: string, variant?: { color?: string; size?: string }) => void;
  updateQuantity: (productId: string, quantity: number, variant?: { color?: string; size?: string }) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(loadStoredCart);

  useEffect(() => {
    const sync = () => {
      if (!isCustomerAuthenticated()) {
        setCartItems([]);
        localStorage.removeItem('cart');
        return;
      }
      setCartItems(loadStoredCart());
    };
    window.addEventListener('nls-auth-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('nls-auth-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (!isCustomerAuthenticated()) {
      localStorage.removeItem('cart');
      return;
    }
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = useCallback((item: CartItem) => {
    if (!isCustomerAuthenticated()) return;
    setCartItems(prev => {
      const itemVariantKey = getVariantKey(item.variant);
      const existing = prev.find(i =>
        i.productId === item.productId && getVariantKey(i.variant) === itemVariantKey
      );
      if (existing) {
        return prev.map(i =>
          i.productId === item.productId && getVariantKey(i.variant) === itemVariantKey
            ? { ...i, quantity: i.quantity + item.quantity }
            : i
        );
      }
      return [...prev, item];
    });
  }, []);

  const removeFromCart = useCallback((productId: string, variant?: { color?: string; size?: string }) => {
    const targetKey = getVariantKey(variant);
    setCartItems(prev => prev.filter(i =>
      !(i.productId === productId && getVariantKey(i.variant) === targetKey)
    ));
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number, variant?: { color?: string; size?: string }) => {
    if (quantity <= 0) {
      removeFromCart(productId, variant);
      return;
    }
    const targetKey = getVariantKey(variant);
    setCartItems(prev =>
      prev.map(i =>
        i.productId === productId && getVariantKey(i.variant) === targetKey
          ? { ...i, quantity }
          : i
      )
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  }, [cartItems]);

  const getCartCount = useCallback(() => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  }, [cartItems]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
